import { useCallback } from 'react'
import { Analytics } from 'hooks/useAnalytics'

import { mixpanelWithCatch } from './mixpanel'
import { SpaceDetailsAnalytics, TokenGatedByAnalytics } from './useGatherSpaceDetailsAnalytics'

////////////////////////////////////////////////////
// For a logged out brand new user w/o redirect:
////////////////////////////////////////////////////
//  (Page event) public town page - post oauth redirect false
//  clicked join town on town page
//  privy modal shown
//  privy login success
//  joining town
//  river connected successfully
//  joined town
//  set username

////////////////////////////////////////////////////
// For a logged out brand new user with redirect:
////////////////////////////////////////////////////
//  (Page event) public town page - post oauth redirect false
//  clicked join town on town page
//  privy modal shown
//  (Page event) public town page - post oauth redirect true - should ignore this event
//  privy login success
//  joining town
// <<<< Optional town with price >>>>
//  river connected successfully
//  joined town
//  set username

////////////////////////////////////////////////////
// For an existing user that was logged out w/o redirect
////////////////////////////////////////////////////
//  (Page event) public town page - post oauth redirect false
//  clicked join town on town page
//  privy modal shown
//  privy login success
//  joining town
// <<<< Optional town with price >>>>
//  river connected successfully
//  joined town
//  set username

////////////////////////////////////////////////////
// For an already logged in user
////////////////////////////////////////////////////
//////////// these 2 events might swap in sequence, it shouldn't matter
//  river connected successfully
//  (Page event) public town page - post oauth redirect false - authenticated true
////////////////
//  clicked join town on town page
//  joining town
// <<<< Optional town with price >>>>
//  joined town

////////////////////////////////////////////////////
// Town with price
////////////////////////////////////////////////////
// ... typical flow
// joining town
// join transaction modal
// click copy wallet address
// ... typical flow

////////////////////////////////////////////////////
// Gated town without meeting requirements (logged in user)
////////////////////////////////////////////////////
//  river connected successfully
//  (Page event) public town page - post oauth redirect false
//  clicked join town on town page - meetsMembershipRequirements = false
//  viewed gated town requirements modal
// clicked link wallet on gated town requirements modal
// successfully linked wallet - meetsMembershipRequirements = true
// clicked join town on gated town requirements modal
// joining town
// <<<< Optional town with price >>>>
// joined town

export const useJoinFunnelAnalytics = () => {
    return {
        publicTownPage: useCallback(
            (args: {
                authenticated: boolean
                spaceId: string | undefined
                spaceName: string | undefined
                pricingModel: 'free' | 'paid'
                postOAuthRedirect: boolean
                gatedSpace: boolean
                pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
            }) => {
                const {
                    authenticated,
                    spaceId,
                    spaceName,
                    pricingModel,
                    postOAuthRedirect,
                    gatedSpace,
                    pricingModule,
                } = args

                const tracked = {
                    authenticated,
                    spaceId,
                    spaceName,
                    pricingModel,
                    // A user may redirect to oauth page, then return to the app and see the public town page again
                    postOAuthRedirect,
                    gatedSpace,
                    pricingModule,
                }

                mixpanelWithCatch((mixpanel) =>
                    mixpanel.track_pageview({
                        category: 'home-page',
                        name: 'public town page',
                        ...tracked,
                    }),
                )

                Analytics.getInstance().page('home-page', 'public town page', tracked)
            },
            [],
        ),

        // this event is tracked either when user is logged out and clicks "join" - actually they are logging in, join logic is handled elsewhere afterwards
        // or when user is logged in and clicks "join"
        // we use other events/props to segment funnels based on auth state
        clickedJoinTownOnTownPage: useCallback(
            (args: {
                meetsMembershipRequirements?: boolean
                spaceId: string | undefined
                pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
                authenticated: boolean
            }) => {
                const { meetsMembershipRequirements, spaceId, pricingModule, authenticated } = args
                const eventStr = 'clicked join town on town page'

                const tracked = {
                    spaceId,
                    pricingModule,
                    authenticated,
                }

                if (meetsMembershipRequirements !== undefined) {
                    mixpanelWithCatch((mixpanel) =>
                        mixpanel.track(eventStr, {
                            ...tracked,
                            meetsMembershipRequirements,
                        }),
                    )
                    Analytics.getInstance().track(eventStr, {
                        ...tracked,
                        meetsMembershipRequirements,
                    })
                } else {
                    mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                    Analytics.getInstance().track(eventStr, tracked)
                }
            },
            [],
        ),

        clickedLinkWalletOnGatedTownRequirementsModal: useCallback(
            (args: { spaceId: string | undefined }) => {
                const { spaceId } = args
                const eventStr = 'clicked link wallet on gated town requirements modal'
                const tracked = {
                    spaceId,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        clickedJoinTownOnGatedTownRequirementsModal: useCallback(
            (args: { spaceId: string | undefined }) => {
                const { spaceId } = args
                const eventStr = 'clicked join town on gated town requirements modal'
                const tracked = {
                    spaceId,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        joiningTown: useCallback(
            (args: { spaceId: string | undefined; spaceName: string | undefined }) => {
                const { spaceId, spaceName } = args
                const eventStr = 'joining town'
                const tracked = {
                    spaceId,
                    spaceName,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),
        joinedTown: useCallback(
            (args: {
                spaceId: string | undefined
                spaceName: string | undefined
                gatedSpace: boolean | undefined
                pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
                priceInWei: string | undefined
                tokensGatedBy: TokenGatedByAnalytics[] | undefined
                duration: string | undefined
            }) => {
                const eventStr = 'joined town'
                const tracked = {
                    ...args,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        setUsernameWhenJoiningTown: useCallback((args: { spaceId: string | undefined }) => {
            const { spaceId } = args
            const eventStr = 'set username when joining town'
            const tracked = {
                spaceId,
            }

            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
            Analytics.getInstance().track(eventStr, tracked)
        }, []),

        privyModalShown: useCallback(() => {
            const eventStr = 'privy modal shown'
            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr))
            Analytics.getInstance().track(eventStr)
        }, []),

        joinTransactionModalShown: useCallback((args: SpaceDetailsAnalytics) => {
            const { spaceId, spaceName, tokensGatedBy, gatedSpace, pricingModule, priceInWei } =
                args
            const eventStr = 'join transaction modal'
            const tracked = {
                spaceId, // space id
                spaceName, // ie 'fei's town'
                tokensGatedBy,
                gatedSpace,
                pricingModule,
                priceInWei,
            }
            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
            Analytics.getInstance().track(eventStr, tracked)
        }, []),

        clickCopyWalletAddress: useCallback(
            (args: { spaceId: string | undefined; spaceName: string | undefined }) => {
                const { spaceId, spaceName } = args
                const eventStr = 'click copy wallet address'
                const tracked = {
                    spaceId, // space id
                    spaceName, // ie 'fei's town'
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        /**
         * @deprecated use clickedTransferEth instead
         */
        clickedPayWithEth: useCallback(
            (args: {
                spaceName: string | undefined
                spaceId: string | undefined
                gatedSpace: boolean
                pricingModule: 'free' | 'fixed' | 'dynamic' | undefined
                priceInWei: string
            }) => {
                const { spaceName, spaceId, gatedSpace, pricingModule, priceInWei } = args

                const eventStr = 'clicked pay with ETH'
                const tracked = {
                    spaceName,
                    spaceId,
                    gatedSpace,
                    pricingModule,
                    priceInWei,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        clickedPayWithCard: useCallback(
            (args: {
                spaceName: string | undefined
                spaceId: string | undefined
                gatedSpace: boolean | undefined
                pricingModule: 'free' | 'fixed' | 'dynamic' | undefined
                priceInWei: string | undefined
            }) => {
                const { spaceName, spaceId, gatedSpace, pricingModule, priceInWei } = args
                const eventStr = 'clicked pay with card'
                const tracked = {
                    spaceName,
                    spaceId,
                    gatedSpace,
                    pricingModule,
                    priceInWei,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        viewedGatedTownRequirementsModal: useCallback(
            (args: { spaceId: string | undefined; meetsMembershipRequirements: boolean }) => {
                const { spaceId, meetsMembershipRequirements } = args
                const category = 'requirements-modal'
                const name = 'viewed gated town requirements modal'
                const tracked = {
                    spaceId,
                    meetsMembershipRequirements,
                }

                mixpanelWithCatch((mixpanel) =>
                    mixpanel.track_pageview({
                        category,
                        name,
                        ...tracked,
                    }),
                )

                // show asset verification modal
                Analytics.getInstance().page(category, name, tracked)
            },
            [],
        ),

        successfullyLinkedWallet: useCallback(
            (args: { spaceId: string | undefined; meetsMembershipRequirements: boolean }) => {
                const { spaceId, meetsMembershipRequirements } = args
                const eventStr = 'successfully linked wallet'
                const tracked = {
                    spaceId,
                    meetsMembershipRequirements,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        // should fire in 2 places in the userop modal:
        // when a user clicks "connect and transfer eth"
        // when a user clicks "pay with eth"
        // connectedWallet is true when a user clicks "pay with eth" - they already have a connected wallet
        clickedTransferEth: useCallback(
            (args: SpaceDetailsAnalytics & { connectedWallet: boolean }) => {
                const eventStr = 'clicked transfer eth'
                const tracked = {
                    ...args,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        ethTransferSubmitted: useCallback(
            (
                args: SpaceDetailsAnalytics & {
                    transferAmount: string
                },
            ) => {
                const eventStr = 'eth transfer submitted'
                const tracked = {
                    ...args,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        ethTransferSuccess: useCallback(
            (args: SpaceDetailsAnalytics & { transferAmount: string }) => {
                const eventStr = 'eth transfer success'
                const tracked = {
                    ...args,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        ethTransferFailure: useCallback(
            (args: SpaceDetailsAnalytics & { transferAmount: string }) => {
                const eventStr = 'eth transfer failure'
                const tracked = {
                    ...args,
                }

                mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
                Analytics.getInstance().track(eventStr, tracked)
            },
            [],
        ),

        connectWalletSuccess: useCallback((args: SpaceDetailsAnalytics) => {
            const eventStr = 'connect wallet success'
            const tracked = {
                ...args,
            }

            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
            Analytics.getInstance().track(eventStr, tracked)
        }, []),

        connectWalletFailure: useCallback((args: SpaceDetailsAnalytics) => {
            const eventStr = 'connect wallet failure'
            const tracked = {
                ...args,
            }

            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
            Analytics.getInstance().track(eventStr, tracked)
        }, []),

        joinTownConfirm: useCallback((args: SpaceDetailsAnalytics) => {
            const eventStr = 'join town confirm'
            const tracked = {
                ...args,
            }

            mixpanelWithCatch((mixpanel) => mixpanel.track(eventStr, tracked))
            Analytics.getInstance().track(eventStr, tracked)
        }, []),
    }
}
